/**
 * The story editor's two views (spec 025): which one opens, and the per-user
 * preferences that remember the view and the studio map's look (D-2, D-9),
 * and the fly-through's page-session camera memory (D-11).
 *
 * Preferences live in `localStorage`, keyed by user id, and are read
 * synchronously so the wrong view never flashes up. Storage that throws or is
 * unavailable reads as "nothing remembered"; a failed write is ignored.
 */

import { readStoredSession } from '../api/session';
import { clampFly, type FlyCameraState } from './flyCamera';

export type StoryView = 'studio' | 'hood';
/** Every view of the story editor: the two remembered ones plus spec 026's Pictures, which is never remembered (026 D-6). */
export type StoryEditorView = StoryView | 'pictures';
export type MapLook = 'path' | 'fly';

export const storyViewKey = (userId: number) => `sm.storyView.${userId}`;
export const storyMapLookKey = (userId: number) => `sm.storyMapLook.${userId}`;

/** Above this many scenes the map opens in Path, whatever look was remembered (D-7). */
export const FLY_SCENE_LIMIT = 40;

/** The signed-in user's id, or 0 when it cannot be read. */
export function sessionUserId(): number {
  try {
    return readStoredSession()?.id ?? 0;
  } catch {
    return 0;
  }
}

/** A guarded `localStorage` read: storage that throws reads as nothing (also spec 029's scene view). */
export function readPreference(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** A guarded `localStorage` write: a failure is ignored. */
export function writePreference(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable: the choice is just not remembered */
  }
}

export const isStoryView = (v: unknown): v is StoryView => v === 'studio' || v === 'hood';
/** A `view` query the editor opens: `studio`, `hood` or `pictures` (026). */
export const isStoryEditorView = (v: unknown): v is StoryEditorView => isStoryView(v) || v === 'pictures';

export function readStoredView(userId: number): StoryView | null {
  const v = readPreference(storyViewKey(userId));
  return isStoryView(v) ? v : null;
}

/** Only the view switch calls this (opening a story never writes). */
export function writeStoredView(userId: number, view: StoryView): void {
  writePreference(storyViewKey(userId), view);
}

export function readMapLook(userId: number): MapLook | null {
  const v = readPreference(storyMapLookKey(userId));
  return v === 'path' || v === 'fly' ? v : null;
}

export function writeMapLook(userId: number, look: MapLook): void {
  writePreference(storyMapLookKey(userId), look);
}

export interface ResolveStoryViewInput {
  /** The URL's `view` query, if any. */
  query: string | null | undefined;
  userId: number;
  /** A brief from the start sheet is waiting for this story. */
  pendingBrief: boolean;
}

/**
 * Which view opens: a pending brief always opens the studio; then a `view`
 * query of `studio`, `hood` or `pictures` (026); then the remembered view;
 * then the studio (D-3). Reads storage, never writes it.
 */
export function resolveStoryView({ query, userId, pendingBrief }: ResolveStoryViewInput): StoryEditorView {
  if (pendingBrief) return 'studio';
  if (isStoryEditorView(query)) return query;
  return readStoredView(userId) ?? 'studio';
}

/** The map's look on entering the studio: remembered, default Path, Path above 40 scenes. */
export function openingMapLook(userId: number, sceneCount: number): MapLook {
  if (sceneCount > FLY_SCENE_LIMIT) return 'path';
  return readMapLook(userId) ?? 'path';
}

/** What the fly-through keeps of a story when it is left (D-11). */
export interface FlyCameraMemory {
  /** The camera's target state when the fly-through was left. */
  camera: FlyCameraState;
  /** The selected scene's node then. */
  selectedNodeId: string | null;
}

/**
 * The fly-through's camera per story, for the page session (D-11): in memory,
 * never in storage, so a reload starts over (D-9).
 */
const flyCameras = new Map<number, FlyCameraMemory>();

/** Leaving the fly-through records its camera and the selected scene for the story. */
export function rememberFlyCamera(storyId: number, camera: FlyCameraState, selectedNodeId: string | null): void {
  flyCameras.set(storyId, { camera: { ...camera }, selectedNodeId });
}

/** The story's remembered camera, clamped to its current `maxLayer`; null on the first entry. */
export function recallFlyCamera(storyId: number, maxLayer: number): FlyCameraMemory | null {
  const m = flyCameras.get(storyId);
  return m ? { camera: clampFly(m.camera, maxLayer), selectedNodeId: m.selectedNodeId } : null;
}

/** Tests: a new page session. */
export function resetFlyCameraMemory(): void {
  flyCameras.clear();
}

/**
 * Words the studio never shows (spec 025 "Words on screen"): Maya's view
 * says scene, path, choice, character, picture, line and map instead, and
 * "expression" for a character's states (spec 026 D-7).
 */
export const STUDIO_BANNED_WORDS = [
  'node', 'edge', 'graph', 'keyframe', 'timeline', 'sprite', 'track', 'JSON', 'condition', 'storyboard', 'costume',
] as const;

/** The banned words in `text` (case-insensitive, whole words, plurals too). */
export function bannedWordsIn(text: string): string[] {
  return STUDIO_BANNED_WORDS.filter((w) => new RegExp(`\\b${w}s?\\b`, 'i').test(text));
}
